import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
    BehaviorSubject,
    Observable,
    Subscription,
    catchError,
    finalize,
    map,
    of,
    shareReplay,
    tap,
    throwError
} from "rxjs";
import { environment } from "src/environments/environment";
import { ProductLicenseResponseSummary } from "./product_licenses.service";
import { normalizeDescopedSelections } from "../utils/product_license_descoped_selections.util";
import { AssessmentTaskScopeChangesService } from "./assessment_task_scope_changes.service";

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface MerchantSummary {
    id: number;
    accountId: number;
    name: string;
    status: string;
    legalName: string | null;
    merchantCode: string | null;
    industry: string | null;
    website: string | null;
    supportPhone: string | null;
    supportEmail: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
    countryCode: string | null;
    primaryContactName: string | null;
    primaryContactTitle: string | null;
    primaryContactEmail: string | null;
    primaryContactPhone: string | null;
    notes: string | null;
    aiInputTokens: number;
    aiOutputTokens: number;
    secureStorageFileCount: number;
    totalFileSizeBytes: number;
    totalLicenses: number;
    activeLicenses: number;
    expiredLicenses: number;
}

export interface MerchantLicenseSummary {
    actorRetainsAssessmentAccess?: boolean;
    id: number;
    merchantId: number | null;
    formType: string;
    displayName: string | null;
    formKey: string | null;
    version: string | null;
    status: string;
    lastUpdatedAt: string | null;
    lastUpdatedBy: string | null;
    responseSummary: ProductLicenseResponseSummary;
    activationDate: string | null;
    expirationDate: string | null;
    descopedSelections: string[];
    assignedIsaId: number | null;
    assignedIsaName: string | null;
    assignedQsaId: number | null;
    assignedQsaName: string | null;
    assignedQaId: number | null;
    assignedQaName: string | null;
    userReaders: number[];
    editorIds: number[];
    merchantRepId: number | null;
    featureSet: string | null;
}

export interface MerchantDetailResponse {
    merchant: MerchantSummary;
    licenses: MerchantLicenseSummary[];
}

export interface LicenseAssignmentPayload {
    assignedIsaId: number | null;
    assignedQsaId: number | null;
    assignedQaId: number | null;
    userReaders: number[];
    editors: number[];
    merchantRepId: number | null;
}

export type MerchantCreatePayload = Partial<MerchantSummary> & { name: string };
export type MerchantUpdatePayload = Partial<
    Omit<MerchantSummary, "id" | "accountId" | "status" | "totalLicenses" | "activeLicenses" | "expiredLicenses">
>;

@Injectable({ providedIn: "root" })
export class MerchantsService {
    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");

    private merchantsSubject = new BehaviorSubject<MerchantSummary[] | null>(null);
    private pendingRequest$: Observable<MerchantSummary[]> | null = null;
    private currentLoadSubscription: Subscription | null = null;

    constructor(
        private readonly http: HttpClient,
        private readonly taskScopeChanges: AssessmentTaskScopeChangesService
    ) {}

    loadMerchants(forceRefresh = false): Observable<MerchantSummary[]> {
        const cached = this.merchantsSubject.value;
        if (!forceRefresh && Array.isArray(cached)) {
            return of(cached);
        }

        if (forceRefresh && this.pendingRequest$) {
            this.currentLoadSubscription?.unsubscribe();
            this.pendingRequest$ = null;
            this.currentLoadSubscription = null;
        }

        if (!this.pendingRequest$) {
            const request$ = this.fetchMerchantsFromApi().pipe(
                map((response) => this.validateMerchantListResponse(response)),
                map((records) => records.map((record) => this.normalizeMerchant(record))),
                map((records) => records.filter((record): record is MerchantSummary => !!record)),
                shareReplay(1),
                finalize(() => {
                    if (this.pendingRequest$ === request$) {
                        this.pendingRequest$ = null;
                    }
                })
            );
            this.pendingRequest$ = request$;

            this.currentLoadSubscription?.unsubscribe();
            this.currentLoadSubscription = request$.subscribe({
                next: (merchants) => this.merchantsSubject.next(merchants),
                error: () => {
                    this.merchantsSubject.next([]);
                    this.currentLoadSubscription = null;
                },
                complete: () => {
                    this.currentLoadSubscription = null;
                }
            });

            return request$;
        }

        return this.pendingRequest$;
    }

    watchMerchants(): Observable<MerchantSummary[]> {
        if (!this.merchantsSubject.value && !this.pendingRequest$) {
            void this.loadMerchants().subscribe({ error: () => {} });
        }

        return this.merchantsSubject.asObservable().pipe(
            map((records) => Array.isArray(records) ? records : [])
        );
    }

    refreshMerchants(): Observable<MerchantSummary[]> {
        return this.loadMerchants(true);
    }

    clearCache(): void {
        this.currentLoadSubscription?.unsubscribe();
        this.currentLoadSubscription = null;
        this.pendingRequest$ = null;
        this.merchantsSubject.next([]);
    }

    createMerchant(payload: MerchantCreatePayload): Observable<MerchantSummary> {
        return this.http
            .post<ApiResponse<MerchantSummary>>(`${this.apiBaseUrl}/merchants`, payload)
            .pipe(
                map((response) => this.validateMerchantResponse(response)),
                map((record) => {
                    const merchant = this.normalizeMerchant(record);
                    if (!merchant) {
                        throw new Error("Unable to create merchant.");
                    }
                    this.emitUpdatedList((current) => [...current, merchant]);
                    return merchant;
                }),
                catchError((error) => throwError(() => new Error(error?.error?.message || error?.message || "Unable to create merchant.")))
            );
    }

    updateMerchant(merchantId: number, payload: MerchantUpdatePayload): Observable<MerchantSummary> {
        return this.http
            .put<ApiResponse<MerchantSummary>>(`${this.apiBaseUrl}/merchants/${merchantId}`, payload)
            .pipe(
                map((response) => this.validateMerchantResponse(response)),
                map((record) => {
                    const merchant = this.normalizeMerchant(record);
                    if (!merchant) {
                        throw new Error("Unable to update merchant.");
                    }
                    this.emitUpdatedList((current) => current.map((entry) => (entry.id === merchant.id ? merchant : entry)));
                    return merchant;
                }),
                catchError((error) => throwError(() => new Error(error?.error?.message || error?.message || "Unable to update merchant.")))
            );
    }

    deleteMerchant(merchantId: number): Observable<void> {
        return this.http
            .post<ApiResponse<unknown>>(`${this.apiBaseUrl}/merchants/${merchantId}/archive`, {})
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Unable to delete merchant.");
                    }
                    this.emitUpdatedList((current) => current.filter((entry) => entry.id !== merchantId));
                }),
                catchError((error) => throwError(() => new Error(error?.error?.message || error?.message || "Unable to delete merchant.")))
            );
    }

    getMerchantLicenses(merchantId: number): Observable<MerchantDetailResponse> {
        return this.http
            .get<ApiResponse<{ merchant: MerchantSummary; licenses: MerchantLicenseSummary[] }>>(
                `${this.apiBaseUrl}/merchants/${merchantId}/product-licenses`
            )
            .pipe(
                map((response) => this.validateMerchantLicensesResponse(response)),
                map(({ merchant, licenses }) => ({
                    merchant: this.normalizeMerchant(merchant)!,
                    licenses: (licenses || [])
                        .map((license) => this.normalizeLicense(license))
                        .filter((license): license is MerchantLicenseSummary => !!license)
                }))
            );
    }

    updateLicenseAssignments(
        merchantId: number,
        licenseId: number,
        payload: LicenseAssignmentPayload,
        isCurrentSession: () => boolean
    ): Observable<MerchantLicenseSummary> {
        return this.http
            .put<ApiResponse<MerchantLicenseSummary>>(
                `${this.apiBaseUrl}/merchants/${merchantId}/product-licenses/${licenseId}/assignments`,
                payload
            )
            .pipe(
                map((response) => {
                    if (!isCurrentSession()) throw new Error("The signed-in session changed.");
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Unable to update assignments.");
                    }

                    const normalized = this.normalizeLicense(response.data);
                    if (!normalized || normalized.id !== licenseId || normalized.merchantId !== merchantId ||
                        typeof response.data.actorRetainsAssessmentAccess !== "boolean") {
                        throw new Error("Unable to update assignments.");
                    }

                    return { ...normalized, actorRetainsAssessmentAccess: response.data.actorRetainsAssessmentAccess };
                }),
                tap((license) => this.taskScopeChanges.assessmentChanged(license.id))
            );
    }

    private fetchMerchantsFromApi(): Observable<ApiResponse<MerchantSummary[]>> {
        return this.http
            .get<ApiResponse<MerchantSummary[]>>(`${this.apiBaseUrl}/merchants`)
            .pipe(
                catchError((error) =>
                    throwError(() => new Error(error?.error?.message || error?.message || "Unable to load merchants."))
                )
            );
    }

    private validateMerchantListResponse(response: ApiResponse<MerchantSummary[]>): MerchantSummary[] {
        if (!response?.success || !Array.isArray(response.data)) {
            throw new Error(response?.message || "Merchants/SPs could not be loaded.");
        }

        return response.data;
    }

    private validateMerchantResponse(response: ApiResponse<MerchantSummary>): MerchantSummary {
        if (!response?.success || !response.data) {
            throw new Error(response?.message || "Merchant/Service Provider request failed.");
        }

        return response.data;
    }

    private validateMerchantLicensesResponse(
        response: ApiResponse<{ merchant: MerchantSummary; licenses: MerchantLicenseSummary[] }>
    ): { merchant: MerchantSummary; licenses: MerchantLicenseSummary[] } {
        if (!response?.success || !response.data) {
            throw new Error(response?.message || "Unable to load merchant licenses.");
        }

        return response.data;
    }

    private normalizeMerchant(record: MerchantSummary | null | undefined): MerchantSummary | null {
        if (!record || typeof record !== "object") {
            return null;
        }

        const id = Number((record as { id?: unknown }).id);
        const accountId = Number((record as { accountId?: unknown }).accountId);
        if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(accountId) || accountId <= 0) {
            return null;
        }

        const normalizeString = (value: unknown): string | null => {
            if (value === null || value === undefined) {
                return null;
            }
            if (typeof value !== "string") {
                return String(value).trim() || null;
            }
            const trimmed = value.trim();
            return trimmed ? trimmed : null;
        };

        const normalizeNumber = (value: unknown): number => {
            const numeric = Number(value);
            return Number.isFinite(numeric) ? Math.max(0, Math.floor(numeric)) : 0;
        };

        const totalLicenses = normalizeNumber((record as { totalLicenses?: unknown }).totalLicenses);
        const activeLicenses = normalizeNumber((record as { activeLicenses?: unknown }).activeLicenses);
        const expiredSource = (record as { expiredLicenses?: unknown }).expiredLicenses;
        const normalizedExpired = normalizeNumber(expiredSource);
        const expiredLicenses =
            expiredSource === undefined || expiredSource === null
                ? Math.max(0, totalLicenses - activeLicenses)
                : normalizedExpired;

        return {
            id,
            accountId,
            name: normalizeString((record as { name?: string }).name) || "Merchant/Service Provider",
            status: normalizeString((record as { status?: string }).status) || "active",
            legalName: normalizeString((record as { legalName?: string }).legalName),
            merchantCode: normalizeString((record as { merchantCode?: string }).merchantCode),
            industry: normalizeString((record as { industry?: string }).industry),
            website: normalizeString((record as { website?: string }).website),
            supportPhone: normalizeString((record as { supportPhone?: string }).supportPhone),
            supportEmail: normalizeString((record as { supportEmail?: string }).supportEmail),
            addressLine1: normalizeString((record as { addressLine1?: string }).addressLine1),
            addressLine2: normalizeString((record as { addressLine2?: string }).addressLine2),
            city: normalizeString((record as { city?: string }).city),
            region: normalizeString((record as { region?: string }).region),
            postalCode: normalizeString((record as { postalCode?: string }).postalCode),
            countryCode: normalizeString((record as { countryCode?: string }).countryCode),
            primaryContactName: normalizeString((record as { primaryContactName?: string }).primaryContactName),
            primaryContactTitle: normalizeString((record as { primaryContactTitle?: string }).primaryContactTitle),
            primaryContactEmail: normalizeString((record as { primaryContactEmail?: string }).primaryContactEmail),
            primaryContactPhone: normalizeString((record as { primaryContactPhone?: string }).primaryContactPhone),
            notes: normalizeString((record as { notes?: string }).notes),
            aiInputTokens: normalizeNumber((record as { aiInputTokens?: unknown; ai_input_tokens?: unknown }).aiInputTokens ?? (record as { ai_input_tokens?: unknown }).ai_input_tokens),
            aiOutputTokens: normalizeNumber((record as { aiOutputTokens?: unknown; ai_output_tokens?: unknown }).aiOutputTokens ?? (record as { ai_output_tokens?: unknown }).ai_output_tokens),
            secureStorageFileCount: normalizeNumber((record as { secureStorageFileCount?: unknown; secure_storage_file_count?: unknown }).secureStorageFileCount ?? (record as { secure_storage_file_count?: unknown }).secure_storage_file_count),
            totalFileSizeBytes: normalizeNumber((record as { totalFileSizeBytes?: unknown; total_file_size_bytes?: unknown }).totalFileSizeBytes ?? (record as { total_file_size_bytes?: unknown }).total_file_size_bytes),
            totalLicenses,
            activeLicenses,
            expiredLicenses
        };
    }

    private normalizeLicense(record: MerchantLicenseSummary | null | undefined): MerchantLicenseSummary | null {
        if (!record || typeof record !== "object") {
            return null;
        }

        const id = Number((record as { id?: unknown }).id);
        if (!Number.isInteger(id) || id <= 0) {
            return null;
        }

        const normalizeString = (value: unknown): string | null => {
            if (value === null || value === undefined) {
                return null;
            }
            if (typeof value !== "string") {
                return String(value).trim() || null;
            }
            const trimmed = value.trim();
            return trimmed ? trimmed : null;
        };

        const normalizeNumber = (value: unknown): number | null => {
            const numeric = Number(value);
            if (!Number.isInteger(numeric) || numeric <= 0) {
                return null;
            }
            return numeric;
        };

        const normalizeReaders = (value: unknown): number[] => {
            if (Array.isArray(value)) {
                return value
                    .map((entry) => normalizeNumber(entry))
                    .filter((entry): entry is number => entry !== null);
            }

            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed)
                        ? parsed
                              .map((entry) => normalizeNumber(entry))
                              .filter((entry): entry is number => entry !== null)
                        : [];
                } catch (error) {
                    return [];
                }
            }

            return [];
        };

        const normalizeUserAssignments = (
            value: unknown,
            recordOverrides?: { editorIds?: unknown; merchantRepId?: unknown; userReaders?: unknown }
        ): { editors: number[]; merchantRepId: number | null; readers: number[] } => {
            const empty = { editors: [], merchantRepId: null, readers: [] };

            if (!value) {
                return {
                    editors: normalizeReaders(recordOverrides?.editorIds),
                    merchantRepId: normalizeNumber(recordOverrides?.merchantRepId),
                    readers: normalizeReaders(recordOverrides?.userReaders)
                };
            }

            let parsed: unknown = value;
            if (typeof parsed === "string") {
                try {
                    parsed = JSON.parse(parsed);
                } catch (error) {
                    return empty;
                }
            }

            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
                return empty;
            }

            const assignments = parsed as {
                editors?: unknown;
                editorIds?: unknown;
                merchant_rep?: unknown;
                merchantRep?: unknown;
                merchantRepId?: unknown;
                readers?: unknown;
                userReaders?: unknown;
            };

            const editors = normalizeReaders(
                assignments.editors ?? assignments.editorIds ?? recordOverrides?.editorIds
            );
            const merchantRepId = normalizeNumber(
                assignments.merchant_rep ??
                    assignments.merchantRep ??
                    assignments.merchantRepId ??
                    recordOverrides?.merchantRepId
            );
            const readers = normalizeReaders(
                assignments.readers ?? assignments.userReaders ?? recordOverrides?.userReaders
            );

            return { editors, merchantRepId, readers };
        };

        const normalizeResponseSummary = (raw: unknown): ProductLicenseResponseSummary => {
            const toCount = (value: unknown): number => {
                const numeric = Number(value);
                if (!Number.isFinite(numeric)) {
                    return 0;
                }

                const truncated = Math.floor(numeric);
                return truncated > 0 ? truncated : 0;
            };

            const normalizeSections = (
                sections: unknown
            ): Record<string, { draftCount: number; approvedCount: number; qsaReviewedCount: number }> => {
                if (!sections || typeof sections !== "object") {
                    return {};
                }

                const result: Record<string, { draftCount: number; approvedCount: number; qsaReviewedCount: number }> = {};

                const assignSection = (key: unknown, value: unknown): void => {
                    if (typeof key !== "string") {
                        return;
                    }

                    const sectionKey = key.trim();
                    if (!sectionKey) {
                        return;
                    }

                    const draftCount = toCount(
                        (value as { draftCount?: unknown; draft_count?: unknown })?.draftCount ??
                            (value as { draft_count?: unknown })?.draft_count
                    );
                    const approvedCount = toCount(
                        (value as { approvedCount?: unknown; approved_count?: unknown })?.approvedCount ??
                            (value as { approved_count?: unknown })?.approved_count
                    );
                    const qsaReviewedCount = toCount(
                        (value as { qsaReviewedCount?: unknown; qsa_reviewed_count?: unknown })?.qsaReviewedCount ??
                            (value as { qsa_reviewed_count?: unknown })?.qsa_reviewed_count
                    );

                    result[sectionKey] = { draftCount, approvedCount, qsaReviewedCount };
                };

                if (sections instanceof Map) {
                    sections.forEach((value, key) => assignSection(key, value));
                    return result;
                }

                if (Array.isArray(sections)) {
                    sections.forEach((entry) => {
                        if (Array.isArray(entry) && entry.length >= 2) {
                            assignSection(entry[0], entry[1]);
                        }
                    });
                    return result;
                }

                Object.entries(sections as Record<string, unknown>).forEach(([key, value]) => assignSection(key, value));

                return result;
            };

            if (!raw || typeof raw !== "object") {
                return {
                    draftCount: 0,
                    approvedCount: 0,
                    qsaReviewedCount: 0,
                    lastUpdatedAt: null,
                    lastUpdatedBy: null,
                    sections: {}
                };
            }

            const draftCount = toCount((raw as { draftCount?: unknown; draft_count?: unknown }).draftCount ?? (raw as { draft_count?: unknown }).draft_count);
            const approvedCount = toCount(
                (raw as { approvedCount?: unknown; approved_count?: unknown }).approvedCount ??
                    (raw as { approved_count?: unknown }).approved_count
            );
            const qsaReviewedCount = toCount(
                (raw as { qsaReviewedCount?: unknown; qsa_reviewed_count?: unknown }).qsaReviewedCount ??
                    (raw as { qsa_reviewed_count?: unknown }).qsa_reviewed_count
            );
            const lastUpdatedRaw =
                (raw as { lastUpdatedAt?: unknown; last_updated_at?: unknown }).lastUpdatedAt ??
                (raw as { last_updated_at?: unknown }).last_updated_at ??
                null;
            let lastUpdatedAt: string | null = null;
            if (typeof lastUpdatedRaw === "string") {
                lastUpdatedAt = lastUpdatedRaw;
            } else if (lastUpdatedRaw instanceof Date && !Number.isNaN(lastUpdatedRaw.getTime())) {
                lastUpdatedAt = lastUpdatedRaw.toISOString();
            }

            const lastUpdatedByRaw =
                (raw as { lastUpdatedBy?: unknown }).lastUpdatedBy ??
                (raw as { last_updated_by?: unknown }).last_updated_by ??
                (raw as { lastUpdatedByDisplayName?: unknown }).lastUpdatedByDisplayName ??
                (raw as { last_updated_by_display_name?: unknown }).last_updated_by_display_name ??
                null;

            const lastUpdatedBy = typeof lastUpdatedByRaw === "string" ? lastUpdatedByRaw.trim() || null : null;

            const sections = normalizeSections(
                (raw as { sections?: unknown; sectionSummaries?: unknown; section_summaries?: unknown }).sections ??
                    (raw as { sectionSummaries?: unknown }).sectionSummaries ??
                    (raw as { section_summaries?: unknown }).section_summaries
            );

            return { draftCount, approvedCount, qsaReviewedCount, lastUpdatedAt, lastUpdatedBy, sections };
        };

        const responseSummary = normalizeResponseSummary(
            (record as { responseSummary?: unknown; response_summary?: unknown }).responseSummary ??
                (record as { response_summary?: unknown }).response_summary
        );

        const deriveFormKey = (formType: string | null): string | null => {
            if (!formType) {
                return null;
            }

            const normalized = formType.toLowerCase();
            if (normalized.includes("saq")) {
                return "saq_form";
            }

            if (normalized.includes("roc")) {
                return "roc_form";
            }

            return null;
        };

        const normalizedFormType = normalizeString((record as { formType?: string }).formType) || "Form";
        const normalizedFormKey =
            normalizeString((record as { formKey?: string }).formKey) ||
            normalizeString((record as { form_key?: string }).form_key) ||
            deriveFormKey(normalizedFormType);
        const userAssignments = normalizeUserAssignments(
            (record as { userAssignments?: unknown; user_assignments?: unknown }).userAssignments ??
                (record as { user_assignments?: unknown }).user_assignments,
            {
                editorIds: (record as { editorIds?: unknown }).editorIds,
                merchantRepId: (record as { merchantRepId?: unknown }).merchantRepId,
                userReaders: (record as { userReaders?: unknown }).userReaders
            }
        );

        return {
            id,
            merchantId: Number((record as { merchantId?: unknown }).merchantId) || null,
            formType: normalizedFormType,
            displayName: normalizeString(
                (record as { displayName?: string; display_name?: string }).displayName ??
                    (record as { display_name?: string }).display_name
            ),
            formKey: normalizedFormKey,
            version: normalizeString((record as { version?: string }).version),
            status: normalizeString((record as { status?: string }).status) || "inactive",
            lastUpdatedAt:
                normalizeString((record as { lastUpdatedAt?: string }).lastUpdatedAt) || responseSummary.lastUpdatedAt,
            lastUpdatedBy:
                normalizeString((record as { lastUpdatedBy?: string }).lastUpdatedBy) || responseSummary.lastUpdatedBy,
            responseSummary,
            activationDate: normalizeString((record as { activationDate?: string }).activationDate),
            expirationDate: normalizeString((record as { expirationDate?: string }).expirationDate),
            descopedSelections: normalizeDescopedSelections(
                (record as { descopedSelections?: unknown; descoped_selections?: unknown }).descopedSelections ??
                    (record as { descoped_selections?: unknown }).descoped_selections
            ),
            assignedIsaId: normalizeNumber((record as { assignedIsaId?: unknown }).assignedIsaId),
            assignedIsaName: normalizeString((record as { assignedIsaName?: string }).assignedIsaName),
            assignedQsaId: normalizeNumber((record as { assignedQsaId?: unknown }).assignedQsaId),
            assignedQsaName: normalizeString((record as { assignedQsaName?: string }).assignedQsaName),
            assignedQaId: normalizeNumber((record as { assignedQaId?: unknown }).assignedQaId),
            assignedQaName: normalizeString((record as { assignedQaName?: string }).assignedQaName),
            userReaders: normalizeReaders((record as { userReaders?: unknown }).userReaders ?? userAssignments.readers),
            editorIds: normalizeReaders(
                (record as { editorIds?: unknown }).editorIds ?? userAssignments.editors
            ),
            merchantRepId: normalizeNumber(
                (record as { merchantRepId?: unknown }).merchantRepId ?? userAssignments.merchantRepId
            ),
            featureSet:
                normalizeString((record as { featureSet?: string }).featureSet) ||
                normalizeString((record as { feature_set?: string }).feature_set) ||
                "free_trial"
        };
    }

    private emitUpdatedList(updateFn: (current: MerchantSummary[]) => MerchantSummary[]): void {
        const current = Array.isArray(this.merchantsSubject.value) ? this.merchantsSubject.value : [];
        const updated = updateFn([...current]);
        this.merchantsSubject.next(updated);
    }
}
